import { AlertTriangleIcon, PointerIcon, BotIcon, InfoIcon, MegaphoneIcon, Repeat2Icon, CodeIcon } from 'lucide-react';
import type { ActionData } from '../../../types/interaction-log-data';
import { formatDuration } from '../../../utils/format';

const isActionFailed = (action: ActionData): boolean => {
  if (!action.result) return false;
  return action.result === 'Timeout';
};

const isActionSuccess = (action: ActionData): boolean => {
  if (!action.result) return false;
  return action.result !== 'Timeout';
};

export const Action = ({
  action,
  isFlowModule = false,
  isModule = false,
  className = ''
}: {
  action: ActionData;
  isFlowModule?: boolean;
  isModule?: boolean;
  className?: string;
}) => {
  const isFailed = isActionFailed(action);
  const isSuccess = isActionSuccess(action);

  return (
    <div
      className={`text-xs py-1 px-1.5 rounded-md border flex items-start justify-between gap-4 ${className}`}
      style={{
        borderColor: isFailed
          ? 'rgb(239, 68, 68)'
          : isFlowModule || isModule
            ? 'rgb(196, 181, 253)'
            : 'rgb(212, 212, 212)',
        backgroundColor: isFailed
          ? 'rgb(254, 242, 242)'
          : isFlowModule || isModule
            ? 'rgb(250, 245, 255)'
            : 'transparent'
      }}
    >
      <div className='font-medium flex flex-col gap-0.5'>
        <div className='flex gap-2 items-center'>
          {action.type === 'InvokeLambdaFunction' && <CodeIcon className='size-3.5 inline-block' />}
          {action.type === 'ConnectParticipantWithLexBot' && <BotIcon className='size-3.5 inline-block' />}
          {action.type === 'MessageParticipant' && <MegaphoneIcon className='size-3.5 inline-block' />}
          {action.type === 'MessageParticipantIteratively' && <Repeat2Icon className='size-3.5 inline-block' />}
          <span
            className='max-w-[24ch] break-words leading-tight'
            style={{
              color: isFailed ? 'rgb(185, 28, 28)' : isFlowModule || isModule ? 'rgb(107, 33, 168)' : 'inherit'
            }}
          >
            {action.displayName}
          </span>
        </div>
        {isFailed && (
          <div className='flex flex-row items-center gap-1 text-xs font-normal' style={{ color: 'rgb(220, 38, 38)' }}>
            <AlertTriangleIcon className='size-3 stroke-2' style={{ color: 'rgb(220, 38, 38)' }} />
            {String(action.result)}
          </div>
        )}
        {action.type === 'GetParticipantInput' && isSuccess && (
          <div
            className='flex flex-row items-center gap-1 text-xs font-normal'
            style={{ color: isFlowModule ? 'rgb(147, 51, 234)' : 'rgb(75, 85, 99)' }}
          >
            <PointerIcon
              className='size-3 stroke-3'
              style={{ color: isFlowModule ? 'rgb(147, 51, 234)' : 'rgb(75, 85, 99)' }}
            />
            <span className='font-mono tabular-nums'>{String(action.result)}</span>
          </div>
        )}
        {action.type === 'ConnectParticipantWithLexBot' && (
          <div
            className='ml-0.5 flex flex-row items-center gap-1 text-xs font-normal'
            style={{ color: isFlowModule ? 'rgb(147, 51, 234)' : 'rgb(75, 85, 99)' }}
          >
            <InfoIcon
              className='size-3 stroke-3'
              style={{ color: isFlowModule ? 'rgb(147, 51, 234)' : 'rgb(75, 85, 99)' }}
            />
            <span className='font-mono tabular-nums'>{String(action.result)}</span>
          </div>
        )}
      </div>
      {action.duration !== undefined && (
        <div
          className='font-mono tabular-nums'
          style={{
            color: isFailed ? 'rgb(220, 38, 38)' : isFlowModule || isModule ? 'rgb(147, 51, 234)' : 'rgb(107, 114, 128)'
          }}
        >
          {formatDuration(action.duration)}
        </div>
      )}
    </div>
  );
};
